import React, { useRef, useLayoutEffect, useState } from 'react';
import { cloudAssetUrl } from '@utils/cloudAssetUrl.util';
import { Columns } from '@components/Columns';
import { Box } from '@components/Box';
import ScrollMagic from 'scrollmagic';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import { useIsMounted } from '@hooks/useIsMounted';
import { gsap } from 'gsap';
import GiftIcon from '@images/cash-fund-registry-icons/gift_icons.svg';
import CashIcon from '@images/cash-fund-registry-icons/icon_cash.svg';
import PlaneIcon from '@images/cash-fund-registry-icons/icon_plane.svg';
import HeartIcon from '@images/cash-fund-registry-icons/icon_heart.svg';
import {
  UniversalRegistryWrapper,
  StyledMobileLoader,
  StyledBackgroundPhoto,
  HeroTextContainer,
  StyledHeroTextColumn,
  StyledTextPairing,
  LottiesContainer,
  StateIconTextContainer,
  StateIconLottie,
  IconTextPairing,
} from './UniversalRegistry.styles';
import { LazyLoadWrapper } from '@components/LazyLoadWrapper';
import { Image } from '@components/Image';
import { useTranslation } from 'react-i18next';

const smallTitleProps = {
  titleVariant: 'featureHed17',
  bodyVariant: 'featureCopy17',
  titleColor: 'humanaDark6',
  alignment: 'center',
} as const;

export const UniversalRegistry = () => {
  const textRef = useRef<HTMLDivElement>(null);
  const scrollController = useScrollController();
  const isMounted = useIsMounted();
  const [isVisible, setIsVisible] = useState(false);
  useLayoutEffect(() => {
    let scene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let UniversalRegistry: ReturnType<typeof gsap.timeline>;

    if (scrollController && isMounted) {
      const initTimeline = () => {
        if (scrollController) {
          UniversalRegistry = gsap.timeline({ paused: true, defaults: { ease: 'none' } });
          UniversalRegistry.to(
            textRef.current,
            {
              y: '250px',
              duration: 400,
            },
            '+=1'
          );
        }
      };
      const initScene = () => {
        scene = new ScrollMagic.Scene({
          triggerElement: '#UniversalRegistry',
          triggerHook: 'onCenter',
          duration: '300%',
        })
          .addTo(scrollController)
          .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
            UniversalRegistry?.progress(event.progress);
          });
      };
      initTimeline();
      initScene();
    }
    const clearScene = () => {
      if (UniversalRegistry) {
        UniversalRegistry.progress(0);
        UniversalRegistry.kill();
      }
      if (scene) {
        scene.destroy(true);
      }
    };
    return clearScene;
  }, [scrollController, isMounted]);
  const { t } = useTranslation('cashFundRegistry');
  const universalRegistrySectionData = t('universalRegistrySection');
  return (
    <UniversalRegistryWrapper id="UniversalRegistry" backgroundMobileImg="cash-fund/universal.jpg" isVisible={isVisible}>
      <StyledMobileLoader>
        <LazyLoadWrapper beforeLoad={() => setIsVisible(true)}>
          <div />
        </LazyLoadWrapper>
      </StyledMobileLoader>
      <StyledBackgroundPhoto
        src={cloudAssetUrl(isVisible ? '' : 'cash-fund/universal.jpg', {
          optimization: 'mild',
        })}
        paddingTop={[0, 0]}
        height={isVisible ? '820px' : '1347px'}
        zoom={!isVisible}
        moveText={true}
        backgroundPosition={isVisible ? 'bottom' : 'top'}
      >
        <Box ref={textRef}>
          <HeroTextContainer marginTop={[0, 0]}>
            <StyledHeroTextColumn centered>
              <Columns.Column width={12}>
                <StyledTextPairing
                  eyebrow={universalRegistrySectionData.eyeBrow}
                  eyebrowColor="humanaDark4"
                  eyebrowVariant="sectionEyeBrow20"
                  eyebrowTagName="h2"
                  title={universalRegistrySectionData.title}
                  titleVariant="hed56"
                  titleColor="humanaDark6"
                  titleTagName="h2"
                  body={universalRegistrySectionData.content}
                  bodyVariant="dek20"
                  bodyColor="neutra5"
                  alignment={'center'}
                />
              </Columns.Column>
            </StyledHeroTextColumn>
            <LottiesContainer marginTop={[10]}>
              <StateIconTextContainer>
                <StateIconLottie>
                  <Image src={GiftIcon} alt="GiftIcon" />
                </StateIconLottie>
                <IconTextPairing title={universalRegistrySectionData.registryTypeGifts} {...smallTitleProps} />
              </StateIconTextContainer>
              <StateIconTextContainer>
                <StateIconLottie>
                  <Image src={CashIcon} alt="CashIcon" />
                </StateIconLottie>
                <IconTextPairing title={universalRegistrySectionData.registryTypeCash} {...smallTitleProps} />
              </StateIconTextContainer>
              <StateIconTextContainer>
                <StateIconLottie>
                  <Image src={PlaneIcon} alt="PlaneIcon" />
                </StateIconLottie>
                <IconTextPairing title={universalRegistrySectionData.registryTypeExperiences} {...smallTitleProps} />
              </StateIconTextContainer>
              <StateIconTextContainer>
                <StateIconLottie>
                  <Image src={HeartIcon} alt="HeartIcon" />
                </StateIconLottie>
                <IconTextPairing title={universalRegistrySectionData.registryTypeCharity} {...smallTitleProps} />
              </StateIconTextContainer>
            </LottiesContainer>
          </HeroTextContainer>
        </Box>
      </StyledBackgroundPhoto>
    </UniversalRegistryWrapper>
  );
};
